import { navigate } from '@reach/router';
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { IfUserHasPermission } from '../../../authorisation/IfUserHasPermission';
import { ReactComponent as AddIcon } from '../../../images/icons/add.svg';
import { ReactComponent as AirSourceHeatPumpIcon } from '../../../images/icons/air-source-heat-pump.svg';
import { ReactComponent as BoreholeHeatPumpIcon } from '../../../images/icons/borehole-heat-pump.svg';
import { ReactComponent as EyeIcon } from '../../../images/icons/eye.svg';
import { ReactComponent as GroundLoopsHeatPumpIcon } from '../../../images/icons/ground-loops-heat-pump.svg';
import { ReactComponent as PencilIcon } from '../../../images/icons/pencil.svg';
import { ReactComponent as RefreshIcon } from '../../../images/icons/refresh.svg';
import { ReactComponent as ReviewIcon } from '../../../images/icons/review.svg';
import { ReactComponent as SolarIcon } from '../../../images/icons/solar.svg';
import { ReactComponent as UploadIcon } from '../../../images/icons/upload.svg';
import { MenuButton, MenuButtonItem } from '../../../shared/MenuButton';
import { Td, Tr } from '../../../shared/Table';
import { colourNeutral05 } from '../../../styling/colours';
import { spacing03 } from '../../../styling/spacing';
import { CustomerQuoteResponse, isQuoteSubmitted } from '../../projects/projectQuotesAndDesignsResponse';
import { projectUrls } from '../../projects/projectUrls';
import { GenerateBillOfMaterialsForQuoteModal } from '../GenerateBillOfMaterialsForQuoteModal';
import { UploadQuoteToNetSuiteModal } from '../UploadQuoteToNetSuiteModal';

type Props = {
  projectId: number;
  quote: CustomerQuoteResponse;
  userCanEditQuotes: boolean;
};

export const CustomerQuoteRow = (props: Props) => {
  const { projectId, quote, userCanEditQuotes } = props;

  const [isGenerateBillOfMaterialsModalOpen, setIsGenerateBillOfMaterialsModalOpen] = useState(false);

  const [isUploadToNetSuiteModalOpen, setIsUploadToNetSuiteModalOpen] = useState(false);

  return (
    <>
      <GenerateBillOfMaterialsForQuoteModal
        projectId={projectId}
        quoteId={quote.quoteId}
        isOpen={isGenerateBillOfMaterialsModalOpen}
        closeModal={() => setIsGenerateBillOfMaterialsModalOpen(false)}
        shouldRegenerate={quote.hasBillOfMaterials}
      />
      <UploadQuoteToNetSuiteModal
        projectId={projectId}
        quoteId={quote.quoteId}
        isOpen={isUploadToNetSuiteModalOpen}
        closeModal={() => setIsUploadToNetSuiteModalOpen(false)}
      />
      <Tr key={quote.quoteId}>
        <Td>
          {quote.name}
          {quote.isDraft && <DraftIndicator>[Draft]</DraftIndicator>}
        </Td>
        <Td>
          <Icons>
            {quote.heatPumpType === 'Air Source' && <AirSourceHeatPumpIcon width={40} height={40} title="Air Source" />}
            {quote.heatPumpType === 'Ground Source with Ground Loops' && (
              <GroundLoopsHeatPumpIcon width={40} height={40} title="Ground Source with Ground Loops" />
            )}
            {quote.heatPumpType === 'Ground Source with Borehole' && (
              <BoreholeHeatPumpIcon width={40} height={40} title="Ground Source with Borehole" />
            )}
            {quote.includesSolar && <SolarIcon width={40} height={40} title="Solar" />}
          </Icons>
        </Td>
        <Td>{quote.underfloorHeatingNetSuiteQuoteReference}</Td>
        <Td>{quote.heatPumpNetSuiteQuoteReference}</Td>
        <Td>{quote.solarNetSuiteQuoteReference}</Td>
        <Td>{quote.createdDateTime}</Td>
        <Td>{quote.createdBy}</Td>
        <Td>{quote.uploadedDateTime}</Td>
        <Td>
          <MenuButton testId={customerQuoteRowMenuButtonTestId(quote.quoteId)}>
            <MenuButtonItem onClick={() => navigate(projectUrls.editQuote(projectId, quote.quoteId))}>
              {userCanEditQuotes && !quote.uploadedDateTime ? <PencilIcon /> : <EyeIcon />}
              {userCanEditQuotes && !quote.uploadedDateTime ? 'Edit' : 'View'} Quote
            </MenuButtonItem>
            {quote.hasBillOfMaterials && (
              <MenuButtonItem onClick={() => navigate(projectUrls.editQuoteBillOfMaterials(projectId, quote.quoteId))}>
                <ReviewIcon />
                {userCanEditQuotes && !isQuoteSubmitted(quote) ? 'Edit BOM' : 'View BOM'}
              </MenuButtonItem>
            )}
            {!isQuoteSubmitted(quote) ? (
              <>
                <IfUserHasPermission requiredPermission="Edit">
                  {!quote.isDraft && (
                    <MenuButtonItem onClick={() => setIsGenerateBillOfMaterialsModalOpen(true)}>
                      <RefreshIcon />
                      {quote.hasBillOfMaterials ? 'Regenerate' : 'Generate'} BOM
                    </MenuButtonItem>
                  )}
                  {!quote.isDraft && quote.hasBillOfMaterials && (
                    <MenuButtonItem onClick={() => setIsUploadToNetSuiteModalOpen(true)}>
                      <UploadIcon />
                      Upload to NetSuite
                    </MenuButtonItem>
                  )}
                </IfUserHasPermission>
              </>
            ) : (
              <IfUserHasPermission requiredPermission="Edit">
                {!quote.isDraft && (
                  <MenuButtonItem onClick={() => navigate(projectUrls.newDesignFromQuote(projectId, quote.quoteId))}>
                    <AddIcon />
                    Create Design
                  </MenuButtonItem>
                )}
                <MenuButtonItem onClick={() => navigate(projectUrls.newQuoteRevision(projectId, quote.quoteId))}>
                  <PencilIcon />
                  Revise Quote
                </MenuButtonItem>
              </IfUserHasPermission>
            )}
          </MenuButton>
        </Td>
      </Tr>
    </>
  );
};

export const customerQuoteRowMenuButtonTestId = (quoteId: number) => `customer-quote-row-menu-button-${quoteId}`;

const Icons = styled.div`
  svg {
    vertical-align: middle;

    &:not(:last-of-type) {
      margin-right: ${spacing03};
    }
  }
`;

export const DraftIndicator = styled.span`
  color: ${colourNeutral05};
  margin-left: ${spacing03};
`;
